// Shared design tokens, icons, and form components — Tailwind + brand colours.

import { useTheme } from "../providers/ThemeProvider";
import { yupResolver } from "@hookform/resolvers/yup";
import { AnimatePresence, motion } from "motion/react";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import * as yup from "yup";
import { useAuth } from "../hooks";
import { httpService } from "../services/httpService";
import { API_CONFIG } from "../shared/constants";
import { ILoginPayload, ISignUpPayload } from "../interfaces";
import brandLogo from "../assets/images/Logo.png";

// ─── HUE kept for the animated mesh background ────────────────────────────────
export const HUE = 225; // matches primary navy hue

// ─── Global keyframes injection ───────────────────────────────────────────────
export function useGlobalStyles() {
  useEffect(() => {
    const fontLink = document.createElement("link");
    fontLink.rel = "stylesheet";
    fontLink.href =
      "https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500&display=swap";
    document.head.appendChild(fontLink);

    const styleId = "arb-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes arb-rise{from{opacity:0;transform:translateY(16px);}to{opacity:1;transform:translateY(0);}}
        @keyframes arb-drift{0%,100%{transform:translate(0,0);}33%{transform:translate(12px,-18px);}66%{transform:translate(-10px,10px);}}
        @keyframes arb-float-slow{0%,100%{transform:translateY(0);}50%{transform:translateY(-14px);}}
        @keyframes arb-float-slower{0%,100%{transform:translateY(0);}50%{transform:translateY(-20px);}}
        @keyframes arb-pulse-ring{0%,100%{opacity:0.4;transform:scale(1);}50%{opacity:0.9;transform:scale(1.35);}}
        @keyframes arb-glow-trail{0%{background-position:200% 0;}100%{background-position:-200% 0;}}
        @keyframes arb-blink{0%,100%{opacity:1;}50%{opacity:0;}}
        .arb-gradient-text{-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;color:transparent;}
        input:-webkit-autofill{
          -webkit-box-shadow:0 0 0 1000px #ffffff inset !important;
          -webkit-text-fill-color:#111827 !important;
          transition:background-color 9999s ease-in-out 0s !important;
        }
        .dark input:-webkit-autofill{
          -webkit-box-shadow:0 0 0 1000px #262626 inset !important;
          -webkit-text-fill-color:#f3f4f6 !important;
        }
        .dark input:-webkit-autofill:focus{
          -webkit-box-shadow:0 0 0 1000px #262626 inset !important;
          -webkit-text-fill-color:#f3f4f6 !important;
        }
        :root{
          --font-geist:'Geist',-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif;
          --font-mono:'Geist Mono','SF Mono',ui-monospace,Menlo,monospace;
        }
      `;
      document.head.appendChild(style);
    }
    return () => { fontLink.remove(); };
  }, []);
}

// ─── Page-level hook ───────────────────────────────────────────────────────────
export function useAuthPage() {
  const { t } = useTranslation();
  const { isAuthenticated, permittedRoutes, user, isVip } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isReferralValid, setIsReferralValid] = useState<boolean | null>(null);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setIsValidating(true);
      httpService
        .get<{ valid: boolean }>(API_CONFIG.path.referralValidate, { code: ref })
        .then((data) => {
          if (data?.valid) { setIsReferralValid(true); setReferralCode(ref); }
          else { setIsReferralValid(false); toast.error(t("auth.referral.invalid") || "Referral code is invalid"); }
        })
        .catch((err: unknown) => {
          setIsReferralValid(false);
          toast.error(err instanceof Error ? err.message : t("auth.referral.error") || "Something went wrong");
        })
        .finally(() => setIsValidating(false));
    } else { setIsReferralValid(false); setReferralCode(null); }
  }, [searchParams, t]);

  const mode = searchParams.get("mode");
  const showSignUpTab = !!(isReferralValid);
  // const showSignUpTab = !!(isReferralValid || mode === "signup");
  const handleSwapToLogin = () => {
    if (isReferralValid) { setIsReferralValid(false); setReferralCode(null); navigate("/auth", { replace: true }); }
  };
  const shouldRedirectToPricing =
    !!user &&
    !isVip &&
    !user.plan;

  const redirectIfAuthenticated =
    isAuthenticated && permittedRoutes.length > 0
      ? <Navigate to={shouldRedirectToPricing ? '/pricing' : (permittedRoutes[0]?.path || '/dashboard')} replace />
      : null;

  return { isValidating, referralCode, showSignUpTab, handleSwapToLogin, navigateTo: navigate, redirectIfAuthenticated };
}

// ─── Password strength ──────────────────────────────────────────────────────────
export function pwStrength(pw: string) {
  const checks = [/[A-Z]/, /[a-z]/, /[0-9]/, /[^A-Za-z0-9]/, /.{8,}/];
  return { score: checks.filter((r) => r.test(pw)).length };
}

// ─── Icons ────────────────────────────────────────────────────────────────────
export const IcoMail = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="16" height="13" rx="2" /><path d="M2 7l8 5 8-5" />
  </svg>
);
export const IcoLock = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="9" width="12" height="9" rx="2" /><path d="M7 9V6a3 3 0 016 0v3" />
  </svg>
);
export const IcoUser = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="7" r="3.5" /><path d="M3 18c0-3.866 3.134-7 7-7s7 3.134 7 7" />
  </svg>
);
export const IcoBldg = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 17V5a2 2 0 012-2h10a2 2 0 012 2v12M3 17h14M8 17v-4h4v4" />
    <rect x="7" y="7" width="2" height="2" /><rect x="11" y="7" width="2" height="2" />
  </svg>
);
export const IcoEye = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d="M1.5 10s3.5-7 8.5-7 8.5 7 8.5 7-3.5 7-8.5 7-8.5-7-8.5-7z" /><circle cx="10" cy="10" r="2.5" />
  </svg>
);
export const IcoEyeOff = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.12 14.12A8.5 8.5 0 0110 16c-5 0-8.5-6-8.5-6a15.6 15.6 0 014.38-4.38M8.11 3.17A8.5 8.5 0 0110 3c5 0 8.5 6 8.5 6a15.7 15.7 0 01-2.04 2.96M2 2l16 16" />
  </svg>
);
export const IcoCheck = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="4,10 8,14 16,6" />
  </svg>
);
export const IcoArrow = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 10h12M11 5l5 5-5 5" />
  </svg>
);
export const IcoSpark = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
    <path d="M10 1l2 6h6l-5 3.5 2 6L10 13l-5 3.5 2-6L2 7h6z" />
  </svg>
);
export const IcoGift = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="8" width="16" height="10" rx="1" /><path d="M2 8h16M10 8V18" />
    <path d="M7 8C5 8 4 6.5 4 5.5a2.5 2.5 0 015 0C9 7 8 8 7 8z" />
    <path d="M13 8c2 0 3-1.5 3-2.5a2.5 2.5 0 00-5 0C11 7 12 8 13 8z" />
  </svg>
);

// ─── Field — fixed 52px height, floating label, no layout shift ───────────────
interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  type?: string;
  autoComplete?: string;
  error?: string;
  success?: boolean;
  leading?: React.ReactNode;
}

export function Field({ label, value, onChange, onBlur, type = "text", autoComplete, error, success, leading }: FieldProps) {
  const { isDark } = useTheme();
  const [focused, setFocused] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword ? (showPw ? "text" : "password") : type;

  // Icon slot is 38px: 12px gap-left + 15px icon + 11px gap-right
  const ICON_W = 38;
  const padLeft = leading ? ICON_W : 12;
  const padRight = isPassword || success ? 38 : 12;

  const wrapCls = [
    "relative h-[52px] rounded-[10px] bg-white dark:bg-neutral-800 border transition-[border-color,box-shadow] duration-150",
    error ? "border-red-400 ring-[3px] ring-red-100 dark:ring-red-900/30"
      : success ? "border-secondary-500 ring-[3px] ring-secondary-100 dark:ring-secondary-900/30"
        : focused ? "border-primary-500 ring-[3px] ring-primary-100 dark:ring-primary-900/30"
          : "border-black/[0.13] dark:border-neutral-600",
  ].join(" ");

  const iconCls = [
    "absolute left-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none transition-colors duration-150 z-[2]",
    focused ? "text-primary-500 dark:text-primary-400" : "text-black/35 dark:text-white/40",
  ].join(" ");

  const labelCls = [
    "absolute pointer-events-none select-none whitespace-nowrap leading-none z-[2]",
    "transition-[top,font-size,color,transform] duration-200 ease-out",
    leading ? "left-[38px]" : "left-3",
    // Base state: centered
    "top-1/2 -translate-y-1/2 text-[13.5px] font-normal text-black/40 dark:text-white/35",
    // Lifted state: top-aligned (via peer focus, content, or autofill)
    "peer-focus:top-[9px] peer-focus:text-[10.5px] peer-focus:font-medium peer-focus:translate-y-0 peer-focus:text-primary-600 dark:peer-focus:text-primary-400",
    "peer-[:not(:placeholder-shown)]:top-[9px] peer-[:not(:placeholder-shown)]:text-[10.5px] peer-[:not(:placeholder-shown)]:font-medium peer-[:not(:placeholder-shown)]:translate-y-0 dark:peer-[:not(:placeholder-shown)]:text-white/50",
    "peer-autofill:top-[9px] peer-autofill:text-[10.5px] peer-autofill:font-medium peer-autofill:translate-y-0",
  ].join(" ");

  return (
    <div>
      <div className={wrapCls}>
        {leading && <span className={iconCls}>{leading}</span>}
        <input
          type={inputType}
          value={value}
          placeholder=" "
          autoComplete={autoComplete}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); onBlur?.(); }}
          className="peer absolute inset-0 border-none outline-none bg-transparent text-[13.5px] text-gray-900 dark:text-gray-100 pt-5 pb-1 [font-family:var(--font-geist)] w-full box-border focus:outline-none focus:ring-0 focus:ring-offset-0"
          style={{
            paddingLeft: padLeft,
            paddingRight: padRight,
            borderRadius: 9,
            WebkitBoxShadow: isDark ? "0 0 0 1000px #262626 inset" : "0 0 0 1000px #ffffff inset",
            WebkitTextFillColor: isDark ? "#f3f4f6" : "#111827",
            caretColor: isDark ? "#f3f4f6" : "#111827",
          }}
        />
        <label className={labelCls}>{label}</label>
        {isPassword && (
          <button type="button" onClick={() => setShowPw((s) => !s)}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 flex items-center text-black/35 dark:text-white/40 hover:text-black/60 dark:hover:text-white/70 transition-colors bg-transparent border-none cursor-pointer z-10">
            {showPw ? <IcoEyeOff size={15} /> : <IcoEye size={15} />}
          </button>
        )}
        {success && !isPassword && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center text-secondary-500">
            <IcoCheck size={14} />
          </span>
        )}
      </div>
      {error && (
        <p className="text-[11.5px] text-red-500 mt-1.5 pl-0.5 animate-[arb-rise_0.2s_ease-out]">{error}</p>
      )}
    </div>
  );
}

// ─── Password meter ─────────────────────────────────────────────────────────────
export function PasswordMeter({ value }: { value: string }) {
  const { score } = pwStrength(value);
  if (!value) return null;
  const segColor =
    score <= 1 ? "#f87171" :
      score === 2 ? "#fb923c" :
        score === 3 ? "#facc15" :
          score === 4 ? "#4ade80" : "#22c55e";
  const label = ["Very weak", "Weak", "Fair", "Good", "Strong"][Math.min(score - 1, 4)] || "Very weak";
  return (
    <div className="mt-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex-1 h-[3px] rounded-full transition-all duration-300"
            style={{ background: i <= score ? segColor : "rgba(0,0,0,0.08)" }} />
        ))}
      </div>
      <p className="text-[10.5px] text-gray-400 mt-1 text-right">{label}</p>
    </div>
  );
}

// ─── Referral pill ──────────────────────────────────────────────────────────────
export function ReferralPill({ code, onDismiss }: { code: string; onDismiss: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -6, scale: 0.96 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-secondary-50 border border-secondary-300 text-[12.5px]"
    >
      <span className="flex items-center gap-1 text-secondary-700 font-medium">
        <IcoGift size={13} /> Referral applied
      </span>
      <code className="font-mono text-[11.5px] text-secondary-800 font-semibold">{code}</code>
      <span className="w-[5px] h-[5px] rounded-full bg-secondary-500 inline-block animate-[arb-pulse-ring_2s_ease-in-out_infinite]" />
      <button onClick={onDismiss}
        className="text-secondary-600 bg-transparent border-none cursor-pointer text-base leading-none px-0.5 flex items-center hover:text-secondary-800 transition-colors">
        ×
      </button>
    </motion.div>
  );
}

// ─── Segmented tabs ─────────────────────────────────────────────────────────────
export function SegTabs({ tab, onChange }: { tab: string; onChange: (t: string) => void }) {
  return (
    <div className="relative grid grid-cols-2 p-1 rounded-[10px] bg-black/[0.035] dark:bg-neutral-800 border border-black/[0.08] dark:border-neutral-700">
      <div aria-hidden
        className={[
          "absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white dark:bg-neutral-700 rounded-[7px] shadow-sm transition-[left] duration-300",
          tab === "login" ? "left-1" : "left-1/2",
        ].join(" ")} />
      {(["login", "signup"] as const).map((k) => (
        <button key={k} onClick={() => onChange(k)}
          className={[
            "relative z-10 border-none bg-transparent cursor-pointer py-2.5 px-3 [font-family:inherit] text-[13px] font-medium tracking-tight transition-colors duration-200",
            tab === k ? "text-gray-900 dark:text-gray-100" : "text-gray-400 dark:text-neutral-400",
          ].join(" ")}>
          {k === "login" ? "Sign in" : "Sign up"}
        </button>
      ))}
    </div>
  );
}

// ─── CTA button — primary → secondary brand gradient ──────────────────────────
export function CtaButton({ children, disabled, loading }: { children: React.ReactNode; disabled?: boolean; loading?: boolean }) {
  return (
    <button type="submit" disabled={disabled}
      className={[
        "w-full py-3.5 px-5 rounded-[10px] text-[14px] font-semibold flex items-center justify-center gap-2",
        "transition-all duration-200 [font-family:inherit] min-h-[49px]",
        disabled
          ? "bg-black/[0.07] dark:bg-white/[0.06] text-black/30 dark:text-white/25 cursor-not-allowed"
          : "bg-gradient-to-r from-primary-500 to-secondary-500 text-white cursor-pointer",
        !disabled ? "shadow-[0_2px_14px_rgba(26,61,124,0.28)] hover:shadow-[0_4px_20px_rgba(26,61,124,0.38)] hover:-translate-y-px active:translate-y-0 active:shadow-[0_1px_6px_rgba(26,61,124,0.2)]" : "",
      ].join(" ")}>
      {loading ? (
        <span className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <span key={i} className="w-1.5 h-1.5 rounded-full bg-primary/80 inline-block"
              style={{ animation: `arb-pulse-ring .8s ease-in-out ${i * 0.15}s infinite` }} />
          ))}
        </span>
      ) : children}
    </button>
  );
}

// ─── Auth panel (login + signup combined) ───────────────────────────────────────
export interface AuthPanelProps {
  referralCode: string | null;
  showSignUpTab: boolean;
  onSwapToLogin: () => void;
  navigateTo: (path: string) => void;
  showLogo?: boolean;
}

export function AuthPanel({ referralCode, showSignUpTab, onSwapToLogin, navigateTo, showLogo = true }: AuthPanelProps) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { login, signup } = useAuth();
  const [tab, setTab] = useState<"login" | "signup">(showSignUpTab ? "signup" : "login");
  const [localReferral, setLocalReferral] = useState(referralCode || "");
  const [referralDismissed, setReferralDismissed] = useState(false);
  const [showReferralInput, setShowReferralInput] = useState(false);
  const loginCaptchaRef = useRef<ReCAPTCHA>(null);
  const signupCaptchaRef = useRef<ReCAPTCHA>(null);
  const [loginCaptchaToken, setLoginCaptchaToken] = useState<string | null>(null);
  const [signupCaptchaToken, setSignupCaptchaToken] = useState<string | null>(null);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isSignupLoading, setIsSignupLoading] = useState(false);

  useEffect(() => { if (showSignUpTab) setTab("signup"); }, [showSignUpTab]);
  useEffect(() => { if (referralCode) { setLocalReferral(referralCode); setReferralDismissed(false); } }, [referralCode]);

  const loginSchema = yup.object({
    email: yup.string().email(t("auth.login.invalidEmail")).required(t("auth.login.emailRequired")),
    password: yup.string().min(6, t("auth.login.passwordMinLength")).required(t("auth.login.passwordRequired")),
  }).required();

  const signupSchema = yup.object({
    name: yup.string().required(t("auth.signup.errors.usernameRequired")).min(3, t("auth.signup.errors.usernameMin")).trim(""),
    email: yup.string().required(t("auth.signup.errors.emailRequired")).email(t("auth.signup.errors.emailInvalid")),
    companyName: yup.string().required(t("auth.signup.errors.companyRequired")).min(2, t("auth.signup.errors.companyMin")).trim(""),
    password: yup.string()
      .required(t("auth.signup.errors.passwordRequired"))
      .min(8, t("auth.signup.errors.passwordMin"))
      .matches(/[A-Z]/, t("auth.signup.errors.passwordUppercase"))
      .matches(/[a-z]/, t("auth.signup.errors.passwordLowercase"))
      .matches(/[0-9]/, t("auth.signup.errors.passwordNumber"))
      .matches(/[^A-Za-z0-9]/, t("auth.signup.errors.passwordSpecial")),
    confirmPassword: yup.string()
      .required(t("auth.signup.errors.confirmPasswordRequired"))
      .oneOf([yup.ref("password")], t("auth.signup.errors.passwordMatch")),
  }).required();

  const loginForm = useForm<ILoginPayload>({ resolver: yupResolver(loginSchema) });
  const signupForm = useForm<ISignUpPayload>({ resolver: yupResolver(signupSchema), mode: "onChange" });

  const switchTab = (next: string) => {
    if (next === tab) return;
    setTab(next as "login" | "signup");
    if (next === "login") onSwapToLogin();
  };

  const handleLoginSubmit = loginForm.handleSubmit(async (data) => {
    try {
      setIsLoginLoading(true);
      await login({ ...data, recaptchaToken: loginCaptchaToken ?? undefined });
      toast.success(t("auth.login.success"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("auth.login.error"));
      loginCaptchaRef.current?.reset(); setLoginCaptchaToken(null);
    } finally { setIsLoginLoading(false); }
  });

  const handleSignupSubmit = signupForm.handleSubmit(async (data) => {
    if (!signupCaptchaToken) { toast.error(t("auth.signup.errors.captchaRequired") || "Please verify the reCAPTCHA"); return; }
    try {
      setIsSignupLoading(true);
      await signup({ ...data, recaptchaToken: signupCaptchaToken, referralCode: localReferral && !referralDismissed ? localReferral : undefined });
      toast.success(t("auth.signup.success"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("auth.signup.error"));
      signupCaptchaRef.current?.reset(); setSignupCaptchaToken(null);
    } finally { setIsSignupLoading(false); }
  });

  const pwValue = signupForm.watch("password") || "";
  const confirmValue = signupForm.watch("confirmPassword") || "";
  const showReferralPill = !!referralCode && !referralDismissed && tab === "signup";

  // Cross-fade class helpers
  const loginCls = [
    "transition-[opacity,transform,filter] duration-300",
    tab === "login"
      ? "opacity-100 translate-y-0 blur-none pointer-events-auto relative"
      : "opacity-0 -translate-y-1.5 blur-[6px] pointer-events-none absolute inset-0",
  ].join(" ");
  const signupCls = [
    "transition-[opacity,transform,filter] duration-300",
    tab === "signup"
      ? "opacity-100 translate-y-0 blur-none pointer-events-auto relative"
      : "opacity-0 translate-y-1.5 blur-[6px] pointer-events-none absolute inset-0",
  ].join(" ");

  return (
    <div className="flex flex-col gap-[18px] w-full [font-family:var(--font-geist)]">
      {showLogo && (
        <div className="flex justify-center -mb-1">
          <a href="https://www.apprevbooster.com" target="_blank" rel="noreferrer">
            <img src={brandLogo} alt="AppRevBooster" className="h-14 object-contain" />
          </a>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <h1 className="m-0 text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
          {tab === "login" ? "Welcome back." : "Create your workspace."}
        </h1>
        <p className="m-0 text-[13px] text-gray-500 dark:text-neutral-400 leading-relaxed">
          {tab === "login"
            ? t("auth.login.subtitle", "Sign in to keep an eye on revenue, spend, and performance.")
            : t("auth.signup.subtitle", "Spin up AppRevBooster in under a minute — no credit card.")}
        </p>
      </div>

      <AnimatePresence>
        {showReferralPill && <ReferralPill code={referralCode!} onDismiss={() => setReferralDismissed(true)} />}
      </AnimatePresence>

      {/* <SegTabs tab={tab} onChange={switchTab} /> */}

      <div className="relative">
        {/* Login form */}
        <div className={loginCls}>
          <form onSubmit={handleLoginSubmit} autoComplete="on" name="login">
            <div className="flex flex-col gap-3">
              <Controller name="email" control={loginForm.control} defaultValue="" render={({ field }) => (
                <Field label="Email" value={field.value} onChange={field.onChange} onBlur={field.onBlur}
                  type="email" autoComplete="username" error={loginForm.formState.errors.email?.message} leading={<IcoMail size={15} />} />
              )} />
              <Controller name="password" control={loginForm.control} defaultValue="" render={({ field }) => (
                <Field label="Password" value={field.value} onChange={field.onChange} onBlur={field.onBlur}
                  type="password" autoComplete="current-password" error={loginForm.formState.errors.password?.message} leading={<IcoLock size={15} />} />
              )} />
              <div className="flex justify-center">
                <ReCAPTCHA ref={loginCaptchaRef} sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                  theme={isDark ? "dark" : "light"}
                  onChange={(token) => setLoginCaptchaToken(token)} onExpired={() => setLoginCaptchaToken(null)} />
              </div>
              <CtaButton disabled={isLoginLoading || !loginCaptchaToken} loading={isLoginLoading}>
                <span>{t("auth.login.loginButton", "Sign in")}</span>
                {!isLoginLoading && <IcoArrow size={16} />}
              </CtaButton>
              <div className="flex justify-center gap-1">
                <span className="text-[12.5px] text-gray-500 dark:text-neutral-400 bg-transparent border-none cursor-pointer font-medium p-0 [font-family:inherit]">{t("auth.login.forgotPasswordTitle", "Forgot password?")}</span>
                <button type="button" onClick={() => navigateTo("/forgot-password")}
                  className="text-[12.5px] text-primary-500 bg-transparent border-none cursor-pointer font-medium p-0 [font-family:inherit] hover:text-primary-700 transition-colors">
                  {t("auth.login.forgotPasswordSubtitle", "Click here to reset.")}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Signup form */}
        <div className={signupCls}>
          <form onSubmit={handleSignupSubmit} autoComplete="on" name="signup">
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <Controller name="name" control={signupForm.control} defaultValue="" render={({ field }) => (
                  <Field label={t("auth.signup.username", "Name")} value={field.value} onChange={field.onChange} onBlur={field.onBlur}
                    autoComplete="name" error={signupForm.formState.errors.name?.message} leading={<IcoUser size={15} />} />
                )} />
                <Controller name="companyName" control={signupForm.control} defaultValue="" render={({ field }) => (
                  <Field label={t("auth.signup.company", "Company")} value={field.value} onChange={field.onChange} onBlur={field.onBlur}
                    autoComplete="organization" error={signupForm.formState.errors.companyName?.message} leading={<IcoBldg size={15} />} />
                )} />
              </div>
              <Controller name="email" control={signupForm.control} defaultValue="" render={({ field }) => (
                <Field label={t("auth.signup.email", "Work email")} value={field.value} onChange={field.onChange} onBlur={field.onBlur}
                  type="email" autoComplete="email" error={signupForm.formState.errors.email?.message} leading={<IcoMail size={15} />} />
              )} />
              <div>
                <Controller name="password" control={signupForm.control} defaultValue="" render={({ field }) => (
                  <Field label={t("auth.signup.password", "Password")} value={field.value} onChange={field.onChange} onBlur={field.onBlur}
                    type="password" autoComplete="new-password" error={signupForm.formState.errors.password?.message} leading={<IcoLock size={15} />} />
                )} />
                <PasswordMeter value={pwValue} />
              </div>
              <Controller name="confirmPassword" control={signupForm.control} defaultValue="" render={({ field }) => (
                <Field label={t("auth.signup.confirmPassword", "Confirm password")} value={field.value} onChange={field.onChange} onBlur={field.onBlur}
                  type="password" autoComplete="new-password"
                  error={signupForm.formState.errors.confirmPassword?.message}
                  success={!!confirmValue && confirmValue === pwValue}
                  leading={<IcoLock size={15} />} />
              )} />

              {!referralCode && (
                <div>
                  {!showReferralInput
                    ? <button type="button" onClick={() => setShowReferralInput(true)}
                      className="text-[12.5px] text-primary-400 bg-transparent border-none cursor-pointer [font-family:inherit] p-0 hover:text-primary-600 transition-colors">
                      Have a referral code?
                    </button>
                    : <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                      <Field label="Referral code" value={localReferral} onChange={setLocalReferral} autoComplete="off" leading={<IcoGift size={15} />} />
                    </motion.div>
                  }
                </div>
              )}

              <div className="flex justify-center">
                <ReCAPTCHA ref={signupCaptchaRef} sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                  theme={isDark ? "dark" : "light"}
                  onChange={(token) => setSignupCaptchaToken(token)} onExpired={() => setSignupCaptchaToken(null)} />
              </div>
              <CtaButton disabled={isSignupLoading || !signupCaptchaToken} loading={isSignupLoading}>
                <span>{t("auth.signup.signupButton", "Create account")}</span>
                {!isSignupLoading && <IcoArrow size={16} />}
              </CtaButton>
            </div>
          </form>
        </div>
      </div>

      {/* <div className="text-center text-[12.5px] text-gray-500 leading-relaxed">
        {tab === "login" ? (
          <>
            New to AppRevBooster?{" "}
            <button onClick={() => switchTab("signup")}
              className="text-gray-900 font-semibold cursor-pointer bg-transparent border-none [font-family:inherit] text-[12.5px] p-0 hover:text-primary-600 transition-colors">
              Create an account
            </button>
          </>
        ) : (
          <>
            <span>
              By continuing you agree to our{" "}
              <a href="https://apprevbooster.com/terms-of-service" target="_blank" rel="noreferrer" className="text-gray-900 font-medium no-underline hover:underline">Terms</a>
              {" "}and{" "}
              <a href="https://apprevbooster.com/privacy-policy" target="_blank" rel="noreferrer" className="text-gray-900 font-medium no-underline hover:underline">Privacy Policy</a>.
            </span>
            <div className="mt-1">
              Already registered?{" "}
              <button onClick={() => switchTab("login")}
                className="text-gray-900 font-semibold cursor-pointer bg-transparent border-none [font-family:inherit] text-[12.5px] p-0 hover:text-primary-600 transition-colors">
                Sign in
              </button>
            </div>
          </>
        )}
      </div> */}
    </div>
  );
}

// ─── Background (clean atmospheric — no animated blobs) ───────────────────────
export function MeshBackground() {
  const { isDark } = useTheme();
  return (
    <div className="absolute inset-0 overflow-hidden z-0">
      {isDark ? (
        <>
          {/* Deep navy-black base */}
          <div className="absolute inset-0" style={{ background: "#05080f" }} />
          {/* Top-left indigo aurora sweep */}
          <div className="absolute inset-0" style={{
            background: `
              radial-gradient(ellipse 70% 55% at -5% -5%, rgba(99,102,241,0.14) 0%, transparent 65%),
              radial-gradient(ellipse 45% 40% at 105% 100%, rgba(20,184,166,0.08) 0%, transparent 65%)
            `,
          }} />
          {/* Soft glow centered behind the auth card (right side) */}
          <div className="absolute inset-0" style={{
            background: `radial-gradient(ellipse 35% 50% at 78% 50%, rgba(26,61,124,0.1) 0%, transparent 70%)`,
          }} />
          {/* Fine dot grid */}
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.055) 1px, transparent 1px)`,
            backgroundSize: "28px 28px",
          }} />
        </>
      ) : (
        <>
          {/* Clean off-white base */}
          <div className="absolute inset-0" style={{ background: "#f4f6fb" }} />
          {/* Top-left blue tint */}
          <div className="absolute inset-0" style={{
            background: `
              radial-gradient(ellipse 70% 55% at -5% -5%, rgba(99,102,241,0.08) 0%, transparent 65%),
              radial-gradient(ellipse 45% 40% at 105% 100%, rgba(20,184,166,0.05) 0%, transparent 65%)
            `,
          }} />
          {/* Fine dot grid */}
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle, rgba(26,61,124,0.1) 1px, transparent 1px)`,
            backgroundSize: "28px 28px",
          }} />
        </>
      )}
    </div>
  );
}
